/**
 * The react-router pattern of the current screen — `/leads/:leadId` rather than
 * `/leads/482` — so a recorded step names its screen, not one record on it.
 *
 * Read from the route definitions themselves: the router's routes are matched against
 * the location and each matched route's own `path` is joined, so the result is exactly
 * what was declared. Rebuilding it by swapping param *values* back for names cannot be
 * right in general — `/bpm/5/guests/5` has two params equal to 5, and a literal segment
 * can equal a param's value — and a wrong pattern never matches on playback.
 */

import { useContext } from 'react';
import { UNSAFE_DataRouterContext, matchRoutes, useLocation } from 'react-router-dom';

/** Join matched routes' paths: relative ones append, an absolute one starts over. */
function joinPaths(paths: Array<string | undefined>): string {
  const segments: string[] = [];
  for (const path of paths) {
    if (!path) continue; // layout and index routes add nothing
    if (path.startsWith('/')) segments.length = 0;
    segments.push(...path.split('/').filter(Boolean));
  }
  return `/${segments.join('/')}`;
}

export function useRoutePattern(): string {
  const location = useLocation();
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  const routes = dataRouter?.router.routes;
  const matches = routes ? matchRoutes(routes, location) : null;
  // Outside a data router (or on an unmatched URL) the path itself is the best answer.
  if (!matches?.length) return location.pathname;
  return joinPaths(matches.map((match) => match.route.path));
}
